import {
  DEFAULT_COLOR,
  DEFAULT_SIZE,
  DEFAULT_VARIANT,
} from '@upright/ui/common/generic/components/button/Button.constants';
import { ButtonContent } from './Button.blocks';
import styles from './Buttons.module.css';
import clsx from 'clsx';
import { forwardRef, type ForwardedRef } from 'react';
import { composeRenderProps, Link } from 'react-aria-components';

import type { Props as ButtonProps } from './Button.types';
import type { CommonLinkProps, SpecificLinkProps } from '../link';

export type Props = Omit<CommonLinkProps & SpecificLinkProps, 'color'> & ButtonProps;

export const LinkButton = forwardRef(function _LinkButton(
  {
    variant = DEFAULT_VARIANT,
    size = DEFAULT_SIZE,
    color = DEFAULT_COLOR,
    startIcon,
    endIcon,
    onPress,
    isDisabled,
    'aria-label': ariaLabel,
    children,
    href,
    replace,
    id,
  }: Props,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  const routerOptions = replace ? { replace } : undefined;
  const isPdf = href ? /\.pdf\/?/.test(href) : false;
  const isExternal = href ? href.startsWith('http') : false;
  const linkProps = isPdf
    ? { download: true }
    : isExternal
      ? {
          target: '_blank',
          rel: 'noopener noreferrer',
        }
      : {};

  return (
    <Link
      id={id}
      className={clsx(styles.root, styles[variant], styles[size], styles[color])}
      href={href}
      routerOptions={routerOptions}
      onPress={onPress}
      aria-label={ariaLabel}
      isDisabled={isDisabled}
      ref={ref}
      {...linkProps}
    >
      {composeRenderProps(children, (_, renderProps) => (
        <ButtonContent startIcon={startIcon} endIcon={endIcon} variant={variant} color={color} isDisabled={isDisabled}>
          {typeof children === 'function' ? children(renderProps) : children}
        </ButtonContent>
      ))}
    </Link>
  );
});
